import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import { FieldRow } from 'components/FormElements';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { Disclosure } from 'further-ui/hooks';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

type Props = {
  disclosure: Disclosure;
  baseCurrency: CurrencyCode;
  transactionCurrency: CurrencyCode;
  fxRate: CurrencyRateDetails | null;
  onRateChange: (rate: CurrencyRateDetails) => void;
};

const CustomRateInputDialog: FC<Props> = ({
  disclosure,
  baseCurrency,
  transactionCurrency,
  fxRate,
  onRateChange,
}) => {
  const form = useForm<{ overrideRate: number; overrideSource: string }>({
    values: {
      overrideRate: fxRate?.overrideRate ?? fxRate?.fetchedRate ?? 1,
      overrideSource: fxRate?.overrideSource ?? '',
    },
    resolver: zodResolver(
      z.object({
        overrideRate: z.number().min(0),
        overrideSource: z.string().optional(),
      }),
    ),
  });

  const onSubmit = (data: { overrideRate: number; overrideSource: string }) => {
    onRateChange({
      ...fxRate,
      overrideRate: data.overrideRate,
      overrideSource: data.overrideSource,
    });
    disclosure.onClose();
  };

  return (
    <ConfirmDialog
      open={disclosure.isOpen}
      onClose={disclosure.onClose}
      maxWidth="xs"
      title="Set transaction rate"
      actions={{
        confirm: {
          label: 'Save and override rate',
          onClick: form.handleSubmit(onSubmit),
        },
        cancel: {
          label: 'Go back',
          onClick: disclosure.onClose,
        },
      }}
      content={
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Column spacing="sm">
            <FieldRow title={baseCurrency} centerTitle>
              <NumberInput value={1} disabled size="small" />
            </FieldRow>
            <FieldRow title={transactionCurrency} centerTitle>
              <Controller
                control={form.control}
                name="overrideRate"
                render={({
                  fieldState: { error },
                  field: { ref, ...rest },
                }) => (
                  <NumberInput
                    {...rest}
                    min={0}
                    decimals={15}
                    error={!!error}
                    helperText={error?.message?.toString()}
                  />
                )}
              />
            </FieldRow>
            <FieldRow title="Source" centerTitle>
              <Controller
                control={form.control}
                name="overrideSource"
                render={({ field }) => <TextField size="small" {...field} />}
              />
            </FieldRow>
          </Column>
        </form>
      }
    />
  );
};

export default CustomRateInputDialog;
